import { SetupStatus, MetaStatus, InitializationStatus } from '../types';

export interface SetupState {
  setupStatus: SetupStatus;
  setupLoading: boolean;
  setupError: string | null;
  metaStatus: MetaStatus;
  metaLoading: boolean;
  metaError: string | null;
  initializationStatus: InitializationStatus;
}

export interface SetupActions {
  checkSetupStatus: () => Promise<void>;
  installLocalCAR: () => Promise<void>;
  fetchMetaVersion: () => Promise<void>;
  fetchAgentsStatus: () => Promise<void>;
  reloadAgents: () => Promise<void>;
  resetErrors: () => void;
  initializeAndFetchDevices: () => Promise<void>;
}

export const createSetupModule = (set: any, get: any) => ({
  setupStatus: {
    isUp: false,
    lastChecked: null,
    error: null,
  },
  setupLoading: false,
  setupError: null,

  metaStatus: {
    version: null,
    agents: [],
    lastChecked: null,
    error: null,
  },
  metaLoading: false,
  metaError: null,

  initializationStatus: {
    isInitialized: false,
    isInitializing: false,
    lastInitialized: null,
    error: null,
  },

  checkSetupStatus: async () => {
    const { sdk } = get();
    if (!sdk) return;

    set({ setupLoading: true, setupError: null });

    try {
      await sdk.setup.isUp();
      set({
        setupStatus: {
          isUp: true,
          lastChecked: new Date(),
          error: null,
        },
        setupLoading: false,
        setupError: null,
      });
    } catch (error) {
      set({
        setupStatus: {
          isUp: false,
          lastChecked: new Date(),
          error: error instanceof Error ? error.message : 'Setup check failed',
        },
        setupLoading: false,
        setupError: error instanceof Error ? error.message : 'Setup check failed',
      });
    }
  },

  installLocalCAR: async () => {
    const { sdk } = get();
    if (!sdk) return;

    set({ setupLoading: true, setupError: null });

    try {
      await sdk.setup.installLocalCAR();
      set({
        setupLoading: false,
        setupError: null,
      });
    } catch (error) {
      set({
        setupLoading: false,
        setupError: error instanceof Error ? error.message : 'Failed to install local CA',
      });
    }
  },

  fetchMetaVersion: async () => {
    const { sdk } = get();
    if (!sdk) return;

    set({ metaLoading: true, metaError: null });

    try {
      const response = await sdk.meta.getVersion();
      set((state: any) => ({
        metaStatus: {
          ...state.metaStatus,
          version: response.version,
          lastChecked: new Date(),
          error: null,
        },
        metaLoading: false,
        metaError: null,
      }));
    } catch (error) {
      set((state: any) => ({
        metaStatus: {
          ...state.metaStatus,
          version: null,
          lastChecked: new Date(),
          error: error instanceof Error ? error.message : 'Failed to fetch version',
        },
        metaLoading: false,
        metaError: error instanceof Error ? error.message : 'Failed to fetch version',
      }));
    }
  },

  fetchAgentsStatus: async () => {
    const { sdk } = get();
    if (!sdk) return;

    set({ metaLoading: true, metaError: null });

    try {
      const agents = await sdk.meta.getAgentsStatus();
      set((state: any) => ({
        metaStatus: {
          ...state.metaStatus,
          agents: agents,
          lastChecked: new Date(),
          error: null,
        },
        metaLoading: false,
        metaError: null,
      }));
    } catch (error) {
      set((state: any) => ({
        metaStatus: {
          ...state.metaStatus,
          agents: [],
          lastChecked: new Date(),
          error: error instanceof Error ? error.message : 'Failed to fetch agents status',
        },
        metaLoading: false,
        metaError: error instanceof Error ? error.message : 'Failed to fetch agents status',
      }));
    }
  },

  reloadAgents: async () => {
    const { sdk } = get();
    if (!sdk) return;

    set({ metaLoading: true, metaError: null });

    try {
      await sdk.meta.reloadAgents();
      // After reload, fetch status again
      const agents = await sdk.meta.getAgentsStatus();
      set((state: any) => ({
        metaStatus: {
          ...state.metaStatus,
          agents: agents,
          lastChecked: new Date(),
          error: null,
        },
        metaLoading: false,
        metaError: null,
      }));
    } catch (error) {
      set((state: any) => ({
        metaStatus: {
          ...state.metaStatus,
          error: error instanceof Error ? error.message : 'Failed to reload agents',
        },
        metaLoading: false,
        metaError: error instanceof Error ? error.message : 'Failed to reload agents',
      }));
    }
  },

  resetErrors: () => {
    set({
      connectionStatus: { ...get().connectionStatus, error: null },
      devicesError: null,
      salesError: null,
      transcriptionError: null,
      setupError: null,
      metaError: null,
    });
  },

  initializeAndFetchDevices: async () => {
    const state = get();

    if (state.initializationStatus.isInitializing) {
      return;
    }

    set((state: any) => ({
      initializationStatus: {
        ...state.initializationStatus,
        isInitializing: true,
        error: null,
      }
    }));

    try {
      get().initializeSDK();

      await new Promise(resolve => setTimeout(resolve, 100));

      await get().fetchDevices();

      set((_state: any) => ({
        initializationStatus: {
          isInitialized: true,
          isInitializing: false,
          lastInitialized: new Date(),
          error: null,
        }
      }));
    } catch (error) {
      console.error('Failed to initialize and fetch devices:', error);
      set((state: any) => ({
        initializationStatus: {
          ...state.initializationStatus,
          isInitializing: false,
          error: error instanceof Error ? error.message : 'Failed to initialize',
        }
      }));
    }
  },
});
